import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  clearAccessToken,
  getAccessToken,
  setAccessToken,
} from "@/shared/api/client";
import type { TokenResponse } from "@/shared/api/types";

const USER_KEY = "cinema.user";

interface AuthUser {
  user_id: number;
  user_name: string;
  super_user: boolean;
  avatar?: string | null;
  level: number;
  wizard?: boolean | null;
}

interface AuthContextValue {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  loginWithToken: (token: TokenResponse, remember?: boolean) => void;
  /** 清除本地 wizard 标志（完成向导后调用） */
  clearWizardFlag: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function loadUser(): AuthUser | null {
  try {
    const raw = sessionStorage.getItem(USER_KEY) ?? localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

function persistUser(user: AuthUser | null, remember: boolean) {
  sessionStorage.removeItem(USER_KEY);
  localStorage.removeItem(USER_KEY);
  if (!user) return;
  const store = remember ? localStorage : sessionStorage;
  store.setItem(USER_KEY, JSON.stringify(user));
}

/** 认证上下文：token + 用户摘要 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => getAccessToken());
  const [user, setUser] = useState<AuthUser | null>(() => loadUser());

  const loginWithToken = useCallback((res: TokenResponse, remember = false) => {
    setAccessToken(res.access_token, remember);
    const next: AuthUser = {
      user_id: res.user_id,
      user_name: res.user_name,
      super_user: res.super_user,
      avatar: res.avatar,
      level: res.level,
      wizard: res.wizard,
    };
    persistUser(next, remember);
    setToken(res.access_token);
    setUser(next);
  }, []);

  const clearWizardFlag = useCallback(() => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, wizard: false };
      const remember = Boolean(localStorage.getItem(USER_KEY));
      persistUser(next, remember);
      return next;
    });
  }, []);

  const logout = useCallback(() => {
    clearAccessToken();
    persistUser(null, false);
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    const onUnauthorized = () => logout();
    window.addEventListener("cinema:unauthorized", onUnauthorized);
    return () =>
      window.removeEventListener("cinema:unauthorized", onUnauthorized);
  }, [logout]);

  const value = useMemo(
    () => ({
      token,
      user,
      isAuthenticated: Boolean(token),
      loginWithToken,
      clearWizardFlag,
      logout,
    }),
    [token, user, loginWithToken, clearWizardFlag, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
